import { useEffect, useState } from "react";
import { adminHttp, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import PropertyForm from "@/components/admin/PropertyForm";

const EMPTY = {
  slug: "",
  title: "",
  location: "",
  area: "",
  price_aed: 0,
  price_usd: 0,
  bedrooms: 0,
  bathrooms: 0,
  size_sqft: 0,
  type: "Villa",
  status: "For Sale",
  description: "",
  features: [],
  images: [],
  featured: false,
};

export default function PropertiesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, "new" = new form, slug = edit form

  const load = () => {
    setLoading(true);
    adminHttp
      .get("/properties")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete property "${slug}"? This cannot be undone.`)) return;
    try {
      await adminHttp.delete(`/admin/properties/${slug}`);
      toast.success("Property deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (editing === "new") {
    return (
      <PropertyForm
        initial={EMPTY}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        mode="create"
      />
    );
  }
  if (editing) {
    const item = items.find((p) => p.slug === editing);
    if (item) {
      return (
        <PropertyForm
          initial={item}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          mode="edit"
        />
      );
    }
  }

  return (
    <div data-testid="admin-properties-panel">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="underline-label mb-2">Listings</div>
          <h2 className="font-display text-3xl text-[#1c1c1c]">Properties</h2>
        </div>
        <button
          data-testid="admin-property-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-3 px-5 py-3 bg-[#c5a059] text-black uppercase text-[10px] tracking-[0.32em] hover:bg-[#f5f0e8] transition-colors"
        >
          <Plus size={14} /> New Property
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#6a6259] text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-[#6a6259] text-sm">No properties yet. Create your first one.</div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div
              key={p.slug}
              data-testid={`admin-property-row-${p.slug}`}
              className="grid grid-cols-12 gap-4 items-center p-4 border border-[#1c1c1c]/10 bg-white/70 hover:border-[#c5a059]/40 transition-colors"
            >
              <div className="col-span-2">
                <div className="aspect-[4/3] bg-[#ede5d5] overflow-hidden">
                  {p.images?.[0] && (
                    <img src={resolveImageUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
                  )}
                </div>
              </div>
              <div className="col-span-7">
                <div className="underline-label mb-1 text-[10px]">{p.type} · {p.status} {p.featured && "· Featured"}</div>
                <div className="font-display text-xl text-[#1c1c1c]">{p.title}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#6a6259] mt-1">{p.location}</div>
              </div>
              <div className="col-span-2 text-right">
                <div className="font-display text-lg text-[#c5a059]">
                  AED {(p.price_aed / 1_000_000).toFixed(1)}M
                </div>
                <div className="text-[10px] text-[#6a6259] mt-1">{p.bedrooms}bd · {p.size_sqft.toLocaleString()} sqft</div>
              </div>
              <div className="col-span-1 flex flex-col gap-2 items-end">
                <button
                  data-testid={`admin-property-edit-${p.slug}`}
                  onClick={() => setEditing(p.slug)}
                  className="p-2 text-[#c5a059] hover:bg-[#c5a059] hover:text-black transition-colors"
                  aria-label="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  data-testid={`admin-property-delete-${p.slug}`}
                  onClick={() => onDelete(p.slug)}
                  className="p-2 text-[#6a6259] hover:bg-red-900 hover:text-white transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
